import { FrequencyType, MonthlyRepeatType } from "../../GeneratedGraphQL";
import { Recurrence, Service } from "../../AppDataProvider/AppDataProvider";

import { TFunction } from "react-i18next";

export type GetRecurringDescriptionOpts = {
  includeServiceName?: boolean;
  locale: string;
  recurrence: Recurrence;
  service: Service;
  startDate: string;
  t: TFunction;
};

/**
 * Takes in a number and returns it with its ordinal appended.
 * i.e 5  => 5th, 1 => 1st etc
 *
 * Future todo
 * This may need to be conditional on the i18next language,
 * ordinals are quite important for communicating the behaviour
 * of the recurrence but each language has it's own set of ordinals
 * and in some cases has no concept of ordinals.
 */
const getOrdinal = (n: number) => {
  const s = ["th", "st", "nd", "rd"],
    v = n % 100;
  return `${n}${s[(v - 20) % 10] || s[v] || s[0]}`;
};

export const getRecurringDescription = (
  options: GetRecurringDescriptionOpts
): string => {
  const { recurrence, startDate, locale, service, t } = options;
  const { frequency, frequencyType, monthlyRepeatType, endDate, occurrences } =
    recurrence;

  const includeServiceName = options.includeServiceName ?? true;

  const recurrenceDate = new Date(startDate);

  const numericDate = Intl.DateTimeFormat(locale, {
    day: "2-digit",
  }).format(recurrenceDate);

  // e.g. Monday
  const dayName = Intl.DateTimeFormat(locale, {
    weekday: "long",
  }).format(recurrenceDate);

  // e.g. 12 February
  const monthDate = Intl.DateTimeFormat(locale, {
    day: "numeric",
    month: "long",
  }).format(recurrenceDate);

  /**
   * Start to construct the recurring sentence.
   */
  const summaryParts = includeServiceName ? [service.name] : [];

  switch (frequencyType) {
    case FrequencyType.Weekly:
      summaryParts.push(
        t("recurring.repeatWeekly", {
          count: frequency,
          dayName: dayName,
        })
      );
      break;

    case FrequencyType.Monthly:
      if (monthlyRepeatType == MonthlyRepeatType.DayOfWeek) {
        summaryParts.push(
          t("recurring.repeatMonthly", {
            count: frequency,
            dateNumber: `${getOrdinal(parseInt(numericDate) / 7)} ${dayName}`,
          })
        );
      } else {
        summaryParts.push(
          t("recurring.repeatMonthly", {
            count: frequency,
            dateNumber: getOrdinal(parseInt(numericDate)),
          })
        );
      }

      break;

    case FrequencyType.Yearly:
      summaryParts.push(
        t("recurring.repeatYearly", {
          count: frequency,
          monthDate: monthDate,
        })
      );
      break;

    case FrequencyType.Daily:
    default:
      summaryParts.push(t("recurring.repeatDaily", { count: frequency }));
      break;
  }

  if (occurrences) {
    summaryParts.push(
      t("recurring.recurringOccurrence", {
        count: occurrences,
      })
    );
  } else if (endDate) {
    summaryParts.push(
      t("recurring.recurringEndsOn", {
        endDate: Intl.DateTimeFormat(locale, {
          dateStyle: "long",
        }).format(new Date(endDate)),
      })
    );
  }

  return summaryParts.join(" ");
};
